import React from 'react';
import { AddOn, Service, QuestionnaireTemplate } from '@/types';
import { CheckSquare, FileText, Square, X } from 'lucide-react';
import { Input } from '@shared/components/ui/Input';
import { Select } from '@shared/components/ui/Select';
import { TextArea } from '@shared/components/ui/TextArea';
import { showAlertModal } from '@shared/components/common/AlertModal';
import { createId } from '@/lib/ids';
import { useAddOnMutations } from '@features/admin/data';
import {
  QUESTIONNAIRE_TIME_UNITS,
  isQuestionnaireSendTimeUnit,
  applyTierPrice
} from './servicesShared';

interface AddOnEditorProps {
  addOn: AddOn;
  serviceOptions: Service[];
  questionnaireOptions: QuestionnaireTemplate[];
  onClose: () => void;
}

export const AddOnEditor: React.FC<AddOnEditorProps> = ({
  addOn: initial,
  serviceOptions,
  questionnaireOptions,
  onClose
}) => {
  const addOnMutations = useAddOnMutations();
  const [editing, setEditing] = React.useState<AddOn>(initial);

  const handleUpdateQuestionnaire = (templateId: string) => {
    if (!templateId) {
      setEditing({ ...editing, questionnaireConfig: undefined });
      return;
    }
    const currentConfig = editing.questionnaireConfig;
    setEditing({
      ...editing,
      questionnaireConfig: {
        templateId,
        sendTimeValue: currentConfig?.sendTimeValue || 24,
        sendTimeUnit: currentConfig?.sendTimeUnit || 'hours'
      }
    });
  };

  const toggleServiceCompatibility = (serviceId: string) => {
    const current = editing.compatibleServiceIds || [];
    setEditing({
      ...editing,
      compatibleServiceIds: current.includes(serviceId)
        ? current.filter((id) => id !== serviceId)
        : [...current, serviceId]
    });
  };

  const handleSave = async () => {
    if (!editing.name) return showAlertModal('Name is required.');
    if (editing.id) {
      await addOnMutations.update(editing);
    } else {
      await addOnMutations.create({
        ...editing,
        id: createId(),
        compatibleServiceIds: editing.compatibleServiceIds || []
      });
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto animate-scale-up">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold text-slate-800">
            {editing.id ? 'Edit Add-on' : 'Add New Add-on'}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Name</label>
            <Input
              className="border-2 border-slate-100 bg-white focus:border-teal-500 font-bold"
              placeholder="e.g. Audiometry"
              value={editing.name}
              onChange={(e) => setEditing({ ...editing, name: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-400 uppercase mb-1">
              Description
            </label>
            <TextArea
              className="border-2 border-slate-100 bg-white focus:border-teal-500"
              placeholder="Brief details about the add-on..."
              value={editing.description}
              onChange={(e) => setEditing({ ...editing, description: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase mb-1">
                Standard Price (£)
              </label>
              <Input
                type="number"
                className="border-2 border-slate-100 bg-white font-bold"
                value={editing.price}
                onChange={(e) => setEditing({ ...editing, price: Number(e.target.value) })}
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase mb-1">
                Duration (min)
              </label>
              <Input
                type="number"
                className="border-2 border-slate-100 bg-white font-bold"
                value={editing.durationMinutes}
                onChange={(e) =>
                  setEditing({ ...editing, durationMinutes: Number(e.target.value) })
                }
              />
            </div>
          </div>

          <div className="bg-teal-50/50 p-4 rounded-xl border border-teal-100 mt-2">
            <p className="text-xs font-black text-teal-800 mb-3 uppercase tracking-widest flex items-center gap-2">
              <FileText className="w-4 h-4 text-teal-600" /> Clinical Form Required?
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-[10px] font-bold text-teal-600 uppercase mb-1">
                  Select Form Template
                </label>
                <Select
                  className="border-2 border-white bg-white shadow-sm focus:border-teal-400 transition-all font-medium"
                  value={editing.questionnaireConfig?.templateId || ''}
                  onChange={(e) => handleUpdateQuestionnaire(e.target.value)}
                >
                  <option value="">No questionnaire required</option>
                  {questionnaireOptions.map((q) => (
                    <option key={q.id} value={q.id}>
                      {q.name}
                    </option>
                  ))}
                </Select>
              </div>
              {editing.questionnaireConfig && (
                <div className="grid grid-cols-2 gap-3 animate-fade-in">
                  <div>
                    <label className="block text-[10px] font-bold text-teal-600 uppercase mb-1">
                      Send Reminder
                    </label>
                    <Input
                      type="number"
                      className="border-2 border-white bg-white focus:border-teal-400"
                      value={editing.questionnaireConfig.sendTimeValue}
                      onChange={(e) =>
                        setEditing({
                          ...editing,
                          questionnaireConfig: {
                            ...editing.questionnaireConfig!,
                            sendTimeValue: Number(e.target.value)
                          }
                        })
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-teal-600 uppercase mb-1">
                      Timing Unit
                    </label>
                    <Select
                      className="border-2 border-white bg-white focus:border-teal-400"
                      value={editing.questionnaireConfig.sendTimeUnit}
                      onChange={(e) =>
                        setEditing({
                          ...editing,
                          questionnaireConfig: {
                            ...editing.questionnaireConfig!,
                            sendTimeUnit: isQuestionnaireSendTimeUnit(e.target.value)
                              ? e.target.value
                              : 'hours'
                          }
                        })
                      }
                    >
                      {QUESTIONNAIRE_TIME_UNITS.map((u) => (
                        <option key={u.value} value={u.value}>
                          {u.label}
                        </option>
                      ))}
                    </Select>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 mt-2">
            <p className="text-xs font-bold text-slate-700 mb-3 uppercase tracking-widest">
              Corporate Tier Pricing
            </p>
            <div className="grid grid-cols-3 gap-2 mb-2">
              {(['bronze', 'silver', 'gold'] as const).map((tier) => (
                <div key={tier}>
                  <label
                    className={`block text-[10px] font-bold uppercase mb-1 ${tier === 'bronze' ? 'text-orange-600' : tier === 'silver' ? 'text-slate-500' : 'text-yellow-600'}`}
                  >
                    {tier.charAt(0).toUpperCase() + tier.slice(1)}
                  </label>
                  <Input
                    type="number"
                    className="border bg-white font-bold"
                    value={editing.tierPrices?.[tier] ?? ''}
                    onChange={(e) =>
                      setEditing({
                        ...editing,
                        tierPrices: applyTierPrice(editing.tierPrices, tier, e.target.value)
                      })
                    }
                  />
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {(['platinum', 'diamond'] as const).map((tier) => (
                <div key={tier}>
                  <label
                    className={`block text-[10px] font-bold uppercase mb-1 ${tier === 'platinum' ? 'text-indigo-600' : 'text-cyan-600'}`}
                  >
                    {tier.charAt(0).toUpperCase() + tier.slice(1)}
                  </label>
                  <Input
                    type="number"
                    className="border bg-white font-bold"
                    value={editing.tierPrices?.[tier] ?? ''}
                    onChange={(e) =>
                      setEditing({
                        ...editing,
                        tierPrices: applyTierPrice(editing.tierPrices, tier, e.target.value)
                      })
                    }
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 mt-2">
            <p className="text-xs font-bold text-slate-700 mb-2 uppercase tracking-widest">
              Service Compatibility
            </p>
            <p className="text-[10px] text-slate-500 mb-3">
              Check the services this add-on can be attached to during booking.
            </p>
            <div className="space-y-1 max-h-40 overflow-y-auto pr-1 custom-scrollbar">
              {serviceOptions.length === 0 ? (
                <div className="text-xs text-slate-400 p-2">No services found.</div>
              ) : (
                serviceOptions.map((s) => (
                  <div
                    key={s.id}
                    onClick={() => toggleServiceCompatibility(s.id)}
                    className="flex items-center gap-3 cursor-pointer hover:bg-white rounded-lg p-2 transition-all group border border-transparent hover:border-slate-100"
                  >
                    {editing.compatibleServiceIds?.includes(s.id) ? (
                      <CheckSquare className="w-5 h-5 text-teal-600" />
                    ) : (
                      <Square className="w-5 h-5 text-slate-300" />
                    )}
                    <span className="text-sm text-slate-700 font-medium group-hover:text-teal-700">
                      {s.name}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-8 pt-4 border-t border-slate-100">
          <button
            onClick={onClose}
            className="px-6 py-2.5 text-slate-500 font-bold hover:bg-slate-50 rounded-xl transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="bg-teal-600 text-white px-8 py-2.5 rounded-xl font-bold hover:bg-teal-700 shadow-lg shadow-teal-600/20 active:scale-95 transition-all"
          >
            Save Add-on
          </button>
        </div>
      </div>
    </div>
  );
};
